import router from '@/router';
import { $searchNodes } from '@/utils';

export const MODEL_VERSIONS = {
  namespaced: true,
  state: {
    facilityId: null,
    versions: [],
    loading: false,
    selectedVersionId: null,
    lastLoadedFacilityId: null,
    modelSizeCache: {},
    selectedAreaIds: [],
  },
  getters: {
    GET_VERSIONS(state) {
      return state.versions;
    },
    GET_LOADING(state) {
      return state.loading;
    },
    GET_SELECTED_VERSION_ID(state) {
      return state.selectedVersionId;
    },
    GET_LATEST_VERSION(state) {
      if (!state.versions.length) return null;
      const sorted = [...state.versions].sort((a, b) => {
        const va = String(a.version || '0');
        const vb = String(b.version || '0');
        return vb.localeCompare(va, undefined, { numeric: true });
      });
      return sorted[0];
    },
    GET_SORTED_VERSIONS(state) {
      return [...state.versions].sort((a, b) => {
        const va = String(a.version || '0');
        const vb = String(b.version || '0');
        return vb.localeCompare(va, undefined, { numeric: true });
      });
    },
    GET_MODEL_SIZE: (state) => (urn) => {
      if (!urn) return null;
      return state.modelSizeCache[urn] ?? null;
    },
    GET_SELECTED_AREA_IDS(state) {
      return state.selectedAreaIds;
    },
    GET_HAS_SELECTED_AREAS(state) {
      return state.selectedAreaIds.length > 0;
    },
  },
  mutations: {
    SET_FACILITY_ID(state, value) {
      state.facilityId = value;
    },
    SET_VERSIONS(state, value) {
      state.versions = value || [];
    },
    SET_LOADING(state, value) {
      state.loading = !!value;
    },
    SET_SELECTED_VERSION_ID(state, value) {
      state.selectedVersionId = value;
    },
    SET_LAST_LOADED_FACILITY_ID(state, value) {
      state.lastLoadedFacilityId = value;
    },
    SET_MODEL_SIZE(state, { urn, size }) {
      if (!urn) return;
      state.modelSizeCache = { ...state.modelSizeCache, [urn]: size };
    },
    SET_SELECTED_AREA_IDS(state, value) {
      state.selectedAreaIds = Array.isArray(value) ? value : [];
    },
    TOGGLE_AREA_SELECTION(state, areaId) {
      if (!areaId) return;
      const exists = state.selectedAreaIds.includes(areaId);
      state.selectedAreaIds = exists
        ? state.selectedAreaIds.filter((id) => id !== areaId)
        : [...state.selectedAreaIds, areaId];
    },
    RESET(state) {
      state.versions = [];
      state.loading = false;
      state.selectedVersionId = null;
      state.lastLoadedFacilityId = null;
      state.modelSizeCache = {};
      state.selectedAreaIds = [];
    },
  },
  actions: {
    async LOAD({ state, commit, dispatch }, facilityId) {
      const id = facilityId || router.currentRoute.params.id;
      if (!id) return;
      commit('SET_FACILITY_ID', id);

      // if already loaded for this facility, skip
      if (state.lastLoadedFacilityId === id && state.versions.length) return;

      commit('SET_LOADING', true);
      try {
        const versions = await dispatch('ADMIN_MODELS/GET_MASTER_VERSIONS', id, { root: true });
        commit('SET_VERSIONS', versions);
        commit('SET_LAST_LOADED_FACILITY_ID', id);
        // Keep selected if exists in new list; otherwise reset
        if (!versions.some(v => v.id === state.selectedVersionId)) {
          commit('SET_SELECTED_VERSION_ID', null);
        }
        // clear selected areas on facility switch
        commit('SET_SELECTED_AREA_IDS', []);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    RESET({ commit }) {
      commit('RESET');
    },
    SET_SELECTED_VERSION_ID({ commit }, value) {
      commit('SET_SELECTED_VERSION_ID', value);
    },
    async LOAD_MODEL_SIZE({ commit, getters, dispatch }, urn) {
      if (!urn) return 0;
      const cached = getters.GET_MODEL_SIZE(urn);
      if (cached != null) return cached;
      try {
        const documentNode = await dispatch('ADMIN_MODELS/GET_MODEL_FULL_DETAILS', urn, { root: true });
        const nodes = $searchNodes(documentNode, { role: 'graphics', mime: 'application/autodesk-svf' });
        let maxSize = 0;
        if (nodes && nodes.length > 0) {
          for (const node of nodes) {
            const size = node.data?.size ?? node?.size ?? 0;
            if (size > maxSize) maxSize = size;
          }
        }
        commit('SET_MODEL_SIZE', { urn, size: maxSize });
        return maxSize;
      } catch (e) {
        commit('SET_MODEL_SIZE', { urn, size: 0 });
        return 0;
      }
    },
    TOGGLE_AREA_SELECTION({ commit }, areaId) {
      commit('TOGGLE_AREA_SELECTION', areaId);
    },
    CLEAR_SELECTED_AREAS({ commit }) {
      commit('SET_SELECTED_AREA_IDS', []);
    },
  },
};


